import React from 'react';
import { Modal } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import { FaTimes, FaCalendarAlt, FaClock } from 'react-icons/fa';
import * as Yup from 'yup';
import CustomInput from '../CustomInput';
import CustomButton from '../CustomButton';
import { createReminder } from '../../Services/User/Chat';
import { showToast } from '../Toast/toast';
import './CreateReminderModal.css';

const reminderValidationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Title is required')
    .min(2, 'Title must be at least 2 characters')
    .max(100, 'Title must not exceed 100 characters'),
  description: Yup.string()
    .required('Description is required')
    .min(5, 'Description must be at least 5 characters')
    .max(500, 'Description must not exceed 500 characters'),
  reminder_date: Yup.string()
    .required('Date is required')
    .test('not-past', 'Date cannot be in the past', function (value) {
      if (!value) return true;
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }),
  reminder_time: Yup.string()
    .required('Time is required')
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
});

const CreateReminderModal = ({ show, onHide, chatId, onSuccess }) => {
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Format date to YYYY-MM-DD
      const formattedDate = values.reminder_date;
      // Format time to HH:mm (24-hour format)
      const formattedTime = values.reminder_time;

      const reminderData = {
        title: values.title,
        description: values.description,
        reminder_date: formattedDate,
        reminder_time: formattedTime,
        chat_id: chatId,
      };

      const response = await createReminder(reminderData);

      if (response.status) {
        showToast('Date created successfully!', 'success');
        resetForm();
        onHide();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        showToast(response.message || 'Failed to create date', 'error');
      }
    } catch (error) {
      console.error('Error creating date:', error);
      showToast(error.message || 'Failed to create date', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <Modal show={show} onHide={onHide} centered className="create-reminder-modal">
      <Modal.Body className="create-reminder-modal-body">
        <button className="modal-close-btn" onClick={onHide}>
          <FaTimes />
        </button>

        <h4 className="create-reminder-title">Create New Date</h4>

        <Formik
          initialValues={{
            title: '',
            description: '',
            reminder_date: '',
            reminder_time: '',
          }}
          validationSchema={reminderValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, handleChange, values }) => {

            return (
              <Form className="create-reminder-form">
                <div className="form-group">
                  <CustomInput
                    type="text"
                    name="title"
                    id="title"
                    onChange={handleChange}
                    value={values.title}
                    placeholder="Enter title"
                    label="Title"
                    required
                    error={touched.title && errors.title ? errors.title : false}
                  />
                </div>

                <div className="form-group">
                  <CustomInput
                    type="textarea"
                    name="description"
                    id="description"
                    className="rounded"
                    onChange={handleChange}
                    value={values.description}
                    placeholder="Enter description"
                    label="Description"
                    required
                    rows={4}
                    error={touched.description && errors.description ? errors.description : false}
                  />
                </div>

                <div className="form-group">
                  <CustomInput
                    type="date"
                    name="reminder_date"
                    id="reminder_date"
                    onChange={handleChange}
                    value={values.reminder_date}
                    placeholder="dd/mm/yyyy"
                    label="Select Date"
                    required
                    min={today}
                    leftIcon={<FaCalendarAlt />}
                    error={touched.reminder_date && errors.reminder_date ? errors.reminder_date : false}
                  />
                </div>

                <div className="form-group">
                  <CustomInput
                    type="time"
                    name="reminder_time"
                    id="reminder_time"
                    onChange={handleChange}
                    value={values.reminder_time}
                    placeholder="Select time"
                    label="Select Time"
                    required
                    leftIcon={<FaClock />}
                    error={touched.reminder_time && errors.reminder_time ? errors.reminder_time : false}
                  />
                </div>

                <div className="form-actions">
                  <CustomButton
                    type="submit"
                    disabled={isSubmitting}
                    className="save-reminder-btn"
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </CustomButton>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default CreateReminderModal;

